import {PropTypes} from 'rlink-react';

import {eachIntersection} from 'rlink-utils/collections';


function isThemable (component) {
  return typeof component.theme === 'function' && Array.isArray(component.theme.classes);
}

function assertThemable (component, componentLabel = 'Component', sourceComponent) {
  if (!isThemable(component)) {
    let sourceMessage = '';
    if (sourceComponent) {
      sourceMessage = ` of component ${sourceComponent.displayName || sourceComponent.name}`;
    }
    throw new Error(`${componentLabel} ${component.displayName || component.name}${sourceMessage} is not themable!`);
  }
}

function mergeChildThemesFromChildThemeObj (themeObj, destination, ownerName) {
  for (let [grandChildThemeKey, grandChildTheme] of Object.entries(themeObj.childThemes)) {
    if (grandChildThemeKey in destination
        && destination[grandChildThemeKey]
          !== grandChildTheme
        && (
          !Array.isArray(destination[grandChildThemeKey])
          || !Array.isArray(grandChildTheme)
        )) {
      let childName = themeObj.component.displayName || themeObj.component.name;
      let grandChildName = grandChildTheme.component && (grandChildTheme.component.displayName || grandChildTheme.component.name);
      throw new Error(`Duplicate child theme key '${grandChildThemeKey}' in component ${ownerName} (The component ${childName} already has ${grandChildName})`);
    }

    destination[grandChildThemeKey] = grandChildTheme;
  }
}

/**
 * Declaring a component as themeable makes it aware of the CSS classes it
 * needs in its own `render()` method. Optionally it can also declare a set of
 * `forwards` - a list of react components used internally (that are specific
 * to itself, i.e. they are not re-used elsewhere) which have their own theme.
 * The `InnerComponent.theme(props)` helper can then be used to forward CSS
 * classes from the outer component's props to the inner one. Only the inner
 * component's declared classes will be forwarded.
 *
 * `childThemes` may only contain themed components that *are* reused elsewhere.
 * These components must have their own independent theme objects passed to
 * themselves from the top-level component.
 *
 * The decorated component will receive a new static method `theme()` (see
 * below), and its `propTypes` definition will include the new `theme` and
 * `childThemes` props.
 *
 * @usage
 * function MyComponent (props) {
 *   let {theme} = props;
 *   return <div className={theme.MyBorder}>
*      <InnerComponent {...InnerComponent.theme(props)}
 *   </div>
 * }
 *
 * themeable(MyComponent, {
 *   classes: ['MyBorder'],
 *   forwards: [InnerComponent],
 * });
 *
 * function InnerComponent (props) {
 *   return <span className={props.theme.InnerHeading}>Hello!</span>;
 * }
 * themeable(InnerComponent, {
 *   classes: ['InnerHeading']
 * })
 *
 * // would have to be used like this:
 * <MyComponent theme={{
 *   MyBorder: css['fancy-border-class'],
 *   InnerHeading: css['big-text-class']
 *   }} />
 *
 * // if used inside another component:
 *
 * function OtherComponent (props) {
 *   return <div className={props.theme.OtherBorder}>
*      <MyComponent {... MyComponent.theme.child(props)} />
 *   </div>;
 * }
 *
 * themeable(OtherComponent, {
 *   classes: ['OtherBorder'],
 *   childThemes: {MyComponent}
 * })
 *
 * // called like this:
 *  <OtherComponent
 *      theme={{
 *        OtherBorder: css['boring-border-class'],
 *      }}
 *      childTheme={{
 *        MyComponent: {
 *          MyBorder: css['fancy-border-class'],
 *          InnerHeading: css['big-text-class'].
 *        },
 *      }} />
 *
 *
 * @param  {React.Component|Function} componentClass   A react component, modified in-place.
 * @param  {Array}  options.classes  List of css classes used directly by ourselves.
 * @param  {Object} options.forwards list of themed private inner components
 * @param  {Object} childThemes      object of themed inner components that are also used elsewhere
 */
export default function themeable (componentClassOrConfig, config) {

  if (config) {
    return themeable(config)(componentClassOrConfig);
  } else {
    return componentClass => {
      config = componentClassOrConfig;
      // here
      let {classes = [], forwards = [], childThemes = {}} = config;
      /**
       *
       * Helper for forwarding classes to an inner component. Call from the inner
       * component to return its theme and childTheme props. (Note: for inner
       * components with childThemes, use `theme.child()` instead.)
       *
       * @param  {Object<*>} props      a react props object
       * @return {{theme: Object<*>, childThemes: Object<Object<*>>}} an inner
       * component's theme and childThemes props.
       */
      componentClass.theme = (props, _useChildTheme) => {
        if (componentClass._themeResults === undefined
            || Object.keys(componentClass._themeResults).length === 0) {
        let theme = {};
        let themeSource = _useChildTheme && _useChildTheme in props.childThemes ? props.childThemes[_useChildTheme] : props.theme;
        for (let className of componentClass.theme.classes) {
          theme[className] = themeSource[className];
        }

        let childThemes = {};
        for (let childThemeKey of Object.keys(componentClass.theme.childThemes)) {
          if (_useChildTheme !== childThemeKey) {
            childThemes[childThemeKey] = props.childThemes[childThemeKey];
          }
        }

          componentClass._themeResults = {theme, childThemes};
        }

        return componentClass._themeResults;
      };

      /**
       * Helper for forwarding child themes to an inner component. Call from the
       * inner component to return its theme and childTheme props. (Note: for
       * private components with forwarded classes, use `.theme()` instead)
       *
       * @param  {Object<*>]} props      react props
       * @param  {string} childThemeName the child theme name, defaults to the
       *  component's name (it is safe to omit this unless you declared the inner
       *  component as a child theme under a different name)
       * @return {{theme: Object<*>, childThemes: Object<Object<*>>}} an inner
       * component's theme and childThemes props.
       */
      componentClass.theme.child = (props, childThemeNameOrChildClassOverrides = {}, childClassOverrides = {}) => {
        if (componentClass._childThemeResults === undefined
            || Object.keys(componentClass._childThemeResults).length === 0) {
        let childThemeName;
        if (typeof childThemeNameOrChildClassOverrides === 'string') {
          childThemeName = childThemeNameOrChildClassOverrides;
        } else {
          childThemeName = componentClass.sharedChildThemeName
            || componentClass.displayName
            || componentClass.name;
          childClassOverrides = childThemeNameOrChildClassOverrides;
        }
        let {theme, childThemes} = componentClass.theme(props, childThemeName);
        let overriddenThemes = {};
        for ( let [name, css] of Object.entries(childClassOverrides)) {
          overriddenThemes[name] = `${theme[name] || ''} ${css}`;
        }

          componentClass._childThemeResults = {
            theme: {...theme, ...overriddenThemes},
            childThemes,
          };
        }

        return componentClass._childThemeResults;
      };

      componentClass.theme.component = componentClass;

      // merge classes
      componentClass.theme.classes = classes;

      if (!Array.isArray(forwards)) {
        console.warn(`DEPRECATED: Please pass theme forwards components as arrays (inside themeable(${componentClass.name}, forwards: ...))`);
        forwards = Object.values(forwards);
      }

      componentClass.theme.childThemes = {};

      componentClass.theme.forwards = forwards;
      if (forwards.length > 0) {
        for (let forwardedComponent of forwards) {
          assertThemable(forwardedComponent, 'Forwarded component', componentClass);
          let duplicates = [];
          for (let duplicate of eachIntersection(componentClass.theme.classes, forwardedComponent.theme.classes) ) {
            duplicates.push(duplicate);
          }

          if (duplicates.length > 0) {
            throw new Error(`Duplicate theme classes [${duplicates.join(', ')}] found in component ${componentClass.name} (from forwarded component ${forwardedComponent.name}). For shared CSS classes, consider using a childTheme instead?`);
          }

          componentClass.theme.classes = componentClass.theme.classes.concat(forwardedComponent.theme.classes);
          mergeChildThemesFromChildThemeObj(
            forwardedComponent.theme,
            componentClass.theme.childThemes,
            componentClass.name
          );
        }
      }

      if (!('propTypes' in componentClass)) {
        componentClass.propTypes = {};
      }

      // set up theme classes proptype validation
      let classesShape = {};
      for (let className of componentClass.theme.classes) {
        classesShape[className] = PropTypes.string.isRequired;
      }

      componentClass.propTypes.theme = PropTypes.shape(classesShape);

      // merge child themes

      if (Object.keys(childThemes).length > 0) {
        let sharedChildThemes = {};
        let unsharedChildThemes = {};
        for (let [childThemeKey, childThemeComponent] of Object.entries(childThemes)) {
          assertThemable(childThemeComponent, 'Child theme component', componentClass);
          if ( childThemeComponent.sharedChildThemeName ) {
            if (childThemeKey in unsharedChildThemes) {
              throw new Error(`Shared child theme key '${childThemeKey}' is already being used by an unshared child theme key!`);
            }
            if ( !(childThemeKey in sharedChildThemes)) {
              sharedChildThemes[childThemeKey] = [];
            }
            sharedChildThemes[childThemeKey].push(childThemeComponent);
          } else {
            if (childThemeKey in sharedChildThemes) {
              throw new Error(`Child theme key '${childThemeKey}' is already a shared child theme key!`);
            }
            unsharedChildThemes[childThemeKey] = childThemeComponent;
          }
        }
        let childThemesWithSharedChildThemes = Object.assign({}, sharedChildThemes, unsharedChildThemes);
        for (let [childThemeKey, childThemeComponents] of Object.entries(childThemesWithSharedChildThemes)) {
          let childThemeObjects = [];
          if (Array.isArray(childThemeComponents)) {
            childThemeObjects = componentClass.theme.childThemes[childThemeKey] = childThemeComponents.map(component => component.theme);
          } else {
            componentClass.theme.childThemes[childThemeKey] = childThemeComponents.theme;
            childThemeObjects = [childThemeComponents.theme];
          }

          for (let childThemeObj of childThemeObjects) {
            mergeChildThemesFromChildThemeObj(
              childThemeObj,
              componentClass.theme.childThemes,
              componentClass.name
            );
          }
        }
      }

      // set up child themes proptype validation
      let childThemeShape = {};
      for (let [childThemeKey, childTheme] of Object.entries(componentClass.theme.childThemes)) {
        if (Array.isArray(childTheme)) {
          // shared theme classes are a union of their prop types
          childThemeShape[childThemeKey] = {};
          for (let theme of childTheme) {
            Object.assign(childThemeShape[childThemeKey], theme.component.propTypes.theme);
          }
        } else {
          childThemeShape[childThemeKey] = childTheme.component.propTypes.theme;
        }
      }
      componentClass.propTypes.childThemes = PropTypes.shape(childThemeShape);
      return componentClass;
    };
  }
}



// WEBPACK FOOTER //
// ./lib/rlink-theme/themeable.js